/* eslint-env browser */
import PropTypes from 'prop-types';
import React from 'react';
import classnames from 'classnames';
import moment from 'moment';
import { findIndex, isEqual, range, get } from 'lodash';
import { messageFormat as message } from '@clubmed/utils';

import { ESCAPE } from 'Utils/keyCodes';
import responsiveness from 'Components/utils/responsive/Responsive.jsx';
import { scrollToElement } from 'Components/utils/scroll/scrollToElement';

import PriceCalendarDayContent from 'Components/widgets/booking-form/price-calendar/day-content/PriceCalendarDayContent.jsx';
import { PriceCalendarPriceDetails } from './price-details/PriceCalendarPriceDetails.jsx';

const NO_HIGHLIGHTED_DAY = -1;

let hasAlreadyRenderedAnOpenDayOnce;

const animationendEvents = {
  animation: 'animationend',
  WebkitAnimation: 'webkitAnimationEnd',
  MozAnimation: 'mozAnimationEnd',
  OAnimation: 'oAnimationEnd',
  msAnimation: 'MSAnimationEnd',
};
const eventActions = {
  addEventListener: 'addEventListener',
  removeEventListener: 'removeEventListener',
};

const propTypes = {
  days: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.number,
      number: PropTypes.number,
      shortName: PropTypes.string,
      offer: PropTypes.string,
      canGoToBooking: PropTypes.bool,
      isCurrentMonth: PropTypes.bool,
      highlighted: PropTypes.bool,
      onClick: PropTypes.func,
      children: PropTypes.node,
    }),
  ),
  weekDayLongNames: PropTypes.arrayOf(PropTypes.string),
  currentDay: PropTypes.number,
  cmsStartingPrice: PropTypes.object,
  isJoyview: PropTypes.bool,
  shouldScrollOnDaySelection: PropTypes.bool,
  onChangeDay: PropTypes.func,
  shouldMobileCalendarStartEvenOnAClosedDay: PropTypes.bool,
  cmsPrice: PropTypes.shape({
    format: PropTypes.shape({
      from: PropTypes.string,
      instead: PropTypes.string,
      unit: PropTypes.string,
    }),
  }),
  bookingConfig: PropTypes.shape({
    form: PropTypes.shape({
      dateFormat: PropTypes.string,
      startDayCalendar: PropTypes.string,
      title: PropTypes.string,
      resortCriteriaTitle: PropTypes.string,
      guestCriteriaTitle: PropTypes.string,
      durationTitle: PropTypes.string,
      selectDuration: PropTypes.string,
      transportFrom: PropTypes.string,
      withoutTransport: PropTypes.string,
      departureDateTitle: PropTypes.string,
      selectChildren: PropTypes.string,
      childrenBirthdayDateFormatPlaceholder: PropTypes.string,
      checkAvailability: PropTypes.string,
      errorMaxGuestsLabel: PropTypes.string,
      errorLabel: PropTypes.string,
      dateValidate: PropTypes.string,
      legalNotice: PropTypes.string,
      selectAdultsLabels: PropTypes.shape({
        _1: PropTypes.string,
        n: PropTypes.string,
        _7: PropTypes.string,
      }),
      selectChildrenLabels: PropTypes.shape({
        _0: PropTypes.string,
        _1: PropTypes.string,
        n: PropTypes.string,
        _7: PropTypes.string,
      }),
    }),
    priceCalendar: PropTypes.shape({
      selectDayLabel: PropTypes.string,
      closePopin: PropTypes.string,
      warningMessage: PropTypes.string,
      resetCalendar: PropTypes.string,
      offers: PropTypes.shape({
        bestPrice: PropTypes.shape({
          label: PropTypes.string,
        }),
        urgentisation: PropTypes.shape({
          label: PropTypes.string,
          rateToDisplay: PropTypes.number,
        }),
      }),
      priceStatus: PropTypes.shape({
        unknown: PropTypes.string,
        closed: PropTypes.string,
        noFlight: PropTypes.string,
        noDeparture: PropTypes.string,
        full: PropTypes.string,
      }),
    }),
  }),
  travelAdvisorConfig: PropTypes.object,
  children: PropTypes.node,
};

const defaultProps = {
  shouldMobileCalendarStartEvenOnAClosedDay: true,
};

export class PriceCalendarDays extends React.Component<Props> {
  constructor(props) {
    super(props);

    const highlightedDayIndex = this.props.currentDay
      ? findIndex(this.props.days, day => day.dayContent.id === this.props.currentDay)
      : NO_HIGHLIGHTED_DAY;

    this.instanceRef = React.createRef();
    this.state = {
      highlightedDayIndex: highlightedDayIndex,
      highlightedDays: this.getHighlightedDays(highlightedDayIndex),
      hasAnimatedDebut: true,
      calendarWasOpen: false,
      currentPrice: null,
      currentOldPrice: null,
    };
  }

  componentDidMount() {
    this.eventManager(eventActions.addEventListener);
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const index = findIndex(nextProps.days, props => props.dayContent.id === nextProps.currentDay);
    if (
      !isEqual(this.props.days, nextProps.days) ||
      !isEqual(this.props.currentDay, nextProps.currentDay)
    ) {
      this.setState({
        hasAnimatedDebut: true,
        highlightedDayIndex: index,
        highlightedDays: this.getHighlightedDays(index),
      });
      if (
        isEqual(this.props.currentMonth, nextProps.currentMonth) &&
        index !== NO_HIGHLIGHTED_DAY
      ) {
        this.setState({
          currentPrice: nextProps.days[index].dayContent.price,
          currentOldPrice: nextProps.days[index].dayContent.oldPrice,
        });
      }
    }
    if (!isEqual(this.props.duration, nextProps.duration)) {
      this.setState({
        currentPrice: null,
        currentOldPrice: null,
        highlightedDayIndex: index,
        highlightedDays: this.getHighlightedDays(index, nextProps.duration),
      });
    }
  }

  componentDidUpdate(prevProps) {
    if (this.shouldScroll && this.props.currentDay !== prevProps.currentDay) {
      this.shouldScroll = false;
      const monthId = parseInt(this.props.currentDay.toString().substring(0, 6), 10);
      this.props.onChangeMonth(monthId || null);
      this.postClickonDayButtonScroll();
    }
  }

  UNSAFE_componentWillMount() {
    if (typeof window !== 'undefined') {
      document.addEventListener('keyup', this.onKeyUp, false);
    }
  }

  componentWillUnmount() {
    this.eventManager(eventActions.removeEventListener);
    if (typeof window !== 'undefined') {
      document.removeEventListener('keyup', this.onKeyUp, false);
    }
  }

  onKeyUp = e => {
    if (e.which === ESCAPE) {
      this.resetAndScrollBack();
    }
  };

  getHighlightedDays(highlightedDayIndex, duration) {
    const highlightedDayIndexStartRange = highlightedDayIndex + 1;
    if (highlightedDayIndex !== NO_HIGHLIGHTED_DAY) {
      return range(
        highlightedDayIndexStartRange,
        highlightedDayIndexStartRange + (duration || this.props.duration),
      );
    }
    return [];
  }

  handleDayClick(index, day, price) {
    return () => {
      if (!day.canGoToBooking) {
        return;
      }

      // already highlighted => off
      if (index === this.state.highlightedDayIndex) {
        this.resetCalendar();
        return;
      }

      // off => highlighted
      // 1) will scroll
      this.shouldScroll = true;

      // 2) select (for the <form>) the day (only for "over the days")
      this.props.onChangeDay(day || null);

      // 3) highlight
      this.setState({
        highlightedDayIndex: index,
        highlightedDays: this.getHighlightedDays(index),
        calendarWasOpen: true,
        currentPrice: day.price,
        currentOldPrice: day.oldPrice,
        i18nFrom: day.from,
        i18nUnit: price.unit,
      });
    };
  }

  closeDay() {
    this.setState({
      highlightedDayIndex: NO_HIGHLIGHTED_DAY,
      highlightedDays: [],
    });
  }

  isSelected(day, index) {
    return index === this.state.highlightedDayIndex;
  }

  postClickonDayButtonScroll = () => {
    if (!this.isMobile() && this.props.shouldScrollOnDaySelection) {
      scrollToElement('.BookingCriteria-submit', 500, null, true);
    }
    if (this.isMobile()) {
      scrollToElement('.PriceCalendarDayContent-container--highlighted', 500);
    }
  };

  eventManager(action) {
    if (!(action in eventActions)) {
      throw new Error('wrong action call');
    }
    const nodeStyle = this.instanceRef.current.style;
    /* eslint-disable prefer-const */
    for (let styleName in animationendEvents) {
      if (styleName in nodeStyle) {
        this.instanceRef.current[action](
          animationendEvents[styleName],
          this.handleAnimationEnd,
          false,
        );
      }
    }
  }

  handleAnimationEnd = () => {
    this.setState({ hasAnimatedDebut: false });
  };

  renderDay(day, index) {
    const { bookingConfig, cmsPrice } = this.props;
    const { dayContent, priceConfig } = day;

    if (this.isMobile()) {
      if (!dayContent.canGoToBooking && !hasAlreadyRenderedAnOpenDayOnce) {
        return null;
      }
      if (dayContent.canGoToBooking) {
        hasAlreadyRenderedAnOpenDayOnce = true;
      }
    }

    const offerName = dayContent.offer
      ? bookingConfig.priceCalendar.offers[dayContent.offer].label
      : null;
    const offerBackgroundColor = dayContent.offer;
    const statusMessage = dayContent.availability
      ? bookingConfig.priceCalendar.priceStatus[dayContent.availability]
      : null;
    const isSelected = this.isSelected(dayContent, index);
    const price = priceConfig && priceConfig.bestPrice;
    const oldPrice = priceConfig && priceConfig.initialPrice;
    return (
      <li
        key={'cellContainerDay' + index}
        className={classnames('PriceCalendarDays-cellContainer', {
          'PriceCalendarDays-cellContainer--highlighted': isSelected,
        })}
      >
        <PriceCalendarDayContent
          bookingConfig={this.props.bookingConfig}
          travelAdvisorConfig={this.props.travelAdvisorConfig}
          canGoToBooking={dayContent.canGoToBooking}
          from={cmsPrice.format.from}
          name={dayContent.name}
          number={dayContent.number}
          offer={offerName}
          offerBackgroundColor={offerBackgroundColor}
          price={price}
          oldPrice={oldPrice}
          isBestPrice={priceConfig ? priceConfig.isBestPriceMonth : false}
          isCurrentMonth={dayContent.isCurrentMonth}
          shortName={dayContent.shortName}
          statusMessage={statusMessage}
          highlighted={isSelected}
          passivelyHighlighted={this.state.highlightedDays.indexOf(index) !== -1}
          highlightedDayIndex={this.state.highlightedDayIndex}
          isLastHighlighted={
            this.state.highlightedDays[this.state.highlightedDays.length - 1] === index
          }
          onClick={this.handleDayClick(index, dayContent, priceConfig)}
        >
          {!this.isMobile() ? this.renderPopin(index) : null}
        </PriceCalendarDayContent>
      </li>
    );
  }

  getWeekDayLongName(day, index) {
    return (
      <li className="PriceCalendarDays-weekDayLongName" key={'weekday' + index}>
        {day}
      </li>
    );
  }

  renderMobilePopinInAListItem() {
    return (
      <li
        key={'cellContainerPopin' + this.state.highlightedDayIndex}
        className={classnames(
          'PriceCalendarDays-cellContainer',
          'PriceCalendarDays-cellContainer--popin',
        )}
      >
        {this.renderPopin(null)}
      </li>
    );
  }

  renderPopin(currentIndex) {
    const index = currentIndex !== null ? currentIndex : this.state.highlightedDayIndex;
    const day = this.props.days[index];
    const position = this.isMobile() ? '' : this.getPopinPosition(index);
    const verticalAlignment = this.isMobile() ? '' : this.getPopinVerticalAlignment(index);
    const priceConfig = day.priceConfig
      ? {
          ...day.priceConfig,
          unit: this.props.cmsPrice.format[day.priceConfig.rawUnit] || '',
        }
      : null;

    return (
      <PriceCalendarPriceDetails
        labelDaySelection={this.props.bookingConfig.priceCalendar.selectDayLabel}
        closeButtonAltText={this.props.bookingConfig.priceCalendar.closePopin}
        warningMessage={this.props.bookingConfig.priceCalendar.warningMessage}
        validateAction={this.inlineDaySelectionActions(day.dayContent)}
        closeAction={this.closeDay}
        position={position}
        verticalAlignment={verticalAlignment}
        cmsPrice={this.props.cmsPrice}
        cmsStartingPrice={this.props.cmsStartingPrice}
        priceConfig={priceConfig}
        isJoyview={this.props.isJoyview}
      />
    );
  }

  inlineDaySelectionActions(day) {
    return () => {
      scrollToElement('.BookingFormStep--2', 500);
      this.props.onChangeDay(day || null);
    };
  }

  getPopinPosition(index) {
    if (index % 7 < 3) {
      return 'right';
    }
    return 'left';
  }

  getPopinVerticalAlignment(index) {
    if ((index + 1) / 7 <= 2) {
      return 'top';
    }
    if ((index + 1) / 7 <= 4) {
      return 'middle';
    }
    if ((index + 1) / 7 <= 5) {
      return 'bottom';
    }
    if ((index + 1) / 7 <= 6) {
      return 'rockBottom';
    }
  }

  resetCalendar() {
    this.props.onChangeDay(null);
    this.setState({
      highlightedDayIndex: NO_HIGHLIGHTED_DAY,
      highlightedDays: [],
      calendarWasOpen: false,
    });
  }

  renderInfoBlock() {
    const arrivalDate = this.props.currentDay;

    const duration = message(get(this.props, 'bookingConfig.form.selectDuration', '')).format({
      duration: this.props.duration,
    });

    return (
      <div className="PriceCalendarDays-infoBlockContainer">
        <div className="PriceCalendarDays-infoBlockScrollTarget" />
        <div className="PriceCalendarDays-infoBlock">
          {this.state.currentPrice ? (
            <div className="PriceCalendarDays-infoBlockPrice">
              <div>{get(this.state, 'i18nFrom', '')}</div>
              <div className="PriceCalendarDays-infoBlockPriceValue text-booking">
                {this.state.currentPrice.formattedValue}
              </div>
              {this.state.currentOldPrice &&
              this.state.currentOldPrice?.value !== this.state.currentPrice?.value ? (
                <s className="PriceCalendarDays-infoBlockOldPriceValue text-booking">
                  {this.state.currentOldPrice.formattedValue}
                </s>
              ) : null}
              <div>{get(this.state, 'i18nUnit', '')}</div>
            </div>
          ) : null}
          <div className="PriceCalendarDays-infoBlockSojourn">
            <div>
              <span>{get(this.props, 'cmsStartingPrice.departureDateLabel', '')}</span>
              <span> </span>
              <span className="PriceCalendarDays-infoBlockSojournValue">
                {moment(arrivalDate, 'YYYYMMDD').format(
                  get(this.props, 'cmsStartingPrice.dateFormat', 'D MMMM YYYY'),
                )}
              </span>
            </div>
            <div>
              <span>{get(this.props, 'bookingConfig.form.durationTitle', '')}</span>
              <span> : </span>
              <span className="PriceCalendarDays-infoBlockSojournValue">{duration}</span>
            </div>
            <div className="PriceCalendarDays-infoBlockReset" onClick={this.resetAndScrollBack}>
              {get(this.props, 'bookingConfig.priceCalendar.resetCalendar', '')}
            </div>
          </div>
        </div>
      </div>
    );
  }

  resetAndScrollBack = () => {
    if (this.props.currentDay) {
      this.resetCalendar();
      scrollToElement('.PriceCalendar', 300);
    }
  };

  getDays() {
    // if the day at index 35 is not currentMonth, that means the days on the whole last line aren't currentMonth either
    // which means we basically don't need to render them, alleviating the calendar as a whole
    if (!get(this.props.days[35], 'dayContent.isCurrentMonth')) {
      return this.props.days.slice(0, 35);
    }
    return this.props.days;
  }

  isMobile() {
    return !get(this.props, 'responsive.isBrowserGreaterThan.M');
  }

  render() {
    hasAlreadyRenderedAnOpenDayOnce = this.props.shouldMobileCalendarStartEvenOnAClosedDay;

    return (
      <div className="PriceCalendarDays">
        <div className="PriceCalendarDays-legends">
          <div className="PriceCalendarDays-legend">
            <span className="PriceCalendarDays-mark bg-booking" />
            <span>{get(this.props, 'bookingConfig.priceCalendar.offers.bestPrice.label', '')}</span>
          </div>
          <div className="PriceCalendarDays-legend">
            <span className="PriceCalendarDays-mark PriceCalendarDays-mark--red" />
            <span>
              {get(this.props, 'bookingConfig.priceCalendar.offers.urgentisation.label', '')}
            </span>
          </div>
        </div>
        {this.props.children}
        <ul className="PriceCalendarDays-listOfWeekDays">
          {this.props.weekDayLongNames.map(this.getWeekDayLongName)}
        </ul>
        <ul
          className={classnames('PriceCalendarDays-days', {
            'PriceCalendarDays--animatedDebut': this.state.hasAnimatedDebut,
          })}
          ref={this.instanceRef}
        >
          {this.getDays().map(this.renderDay, this)}
        </ul>
        {this.props.currentDay ? this.renderInfoBlock() : null}
      </div>
    );
  }
}

PriceCalendarDays.propTypes = propTypes;
PriceCalendarDays.defaultProps = defaultProps;

export default responsiveness(PriceCalendarDays);
